import { useEffect } from 'react';

interface SchemaProps {
  type: 'organization' | 'website' | 'service' | 'faq';
  data?: any;
}

export default function SEOSchema({ type, data }: SchemaProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${siteUrl}/#organization`,
          name: "Access Point Advisors LLC",
          url: siteUrl,
          logo: {
            "@type": "ImageObject",
            url: "https://public.readdy.ai/ai/img_res/a3e9a384-3fd2-411f-b961-06e957112ff2.png"
          },
          description: "Access Point Advisors LLC specializes in cross-border business consulting, connecting San Francisco companies with international markets in China, Hong Kong, Southeast Asia, South America, and Canada through strategic advisory services and extensive Consulate relationships.",
          address: {
            "@type": "PostalAddress",
            addressLocality: "San Francisco",
            addressRegion: "CA",
            addressCountry: "US"
          },
          areaServed: [
            {
              "@type": "Country",
              name: "United States"
            },
            {
              "@type": "Country",
              name: "China"
            },
            {
              "@type": "Country",
              name: "Hong Kong"
            },
            {
              "@type": "Place",
              name: "Southeast Asia"
            },
            {
              "@type": "Place",
              name: "South America"
            },
            {
              "@type": "Country",
              name: "Canada"
            },
            {
              "@type": "Place",
              name: "Asia Pacific"
            },
            {
              "@type": "Place",
              name: "Latin America"
            },
            {
              "@type": "Place",
              name: "Europe"
            }
          ],
          knowsAbout: [
            "Cross-Border Business Consulting",
            "International Market Entry",
            "China Market Strategy",
            "Hong Kong Business Advisory",
            "Southeast Asia Expansion",
            "Latin America Trade",
            "Canada Business Development",
            "Consulate Relations",
            "Trade Office Partnerships",
            "Global Business Strategy",
            "International Trade Facilitation"
          ],
          slogan: "Your Gateway to Global Cross-Border Opportunities"
        },
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          url: siteUrl,
          name: "Access Point Advisors LLC",
          publisher: {
            "@id": `${siteUrl}/#organization`
          }
        },
        {
          "@type": "Service",
          "@id": `${siteUrl}/#cross-border-consulting`,
          serviceType: "Cross-Border Business Consulting",
          provider: {
            "@id": `${siteUrl}/#organization`
          },
          areaServed: [
            {
              "@type": "Country",
              name: "China"
            },
            {
              "@type": "Country",
              name: "Hong Kong"
            },
            {
              "@type": "Place",
              name: "Southeast Asia"
            },
            {
              "@type": "Place",
              name: "South America"
            },
            {
              "@type": "Country",
              name: "Canada"
            }
          ],
          description: "Specialized cross-border consulting services connecting San Francisco businesses with international markets in China, Hong Kong, Southeast Asia, South America, and Canada. Leveraging extensive relationships with Consulate officers and trade offices across Asia, North America, South America, and Europe for seamless international business expansion and trade facilitation.",
          offers: {
            "@type": "Offer",
            availability: "https://schema.org/InStock"
          }
        },
        {
          "@type": "Service",
          serviceType: "International Market Entry Strategy",
          provider: {
            "@id": `${siteUrl}/#organization`
          },
          description: "Expert guidance for entering and expanding into China, Hong Kong, Southeast Asia, Latin America, and Canadian markets with strategic planning and local market insights."
        },
        {
          "@type": "Service",
          serviceType: "Consulate and Trade Office Relations",
          provider: {
            "@id": `${siteUrl}/#organization`
          },
          description: "Extensive relationships with San Francisco Consulate officers and trade offices spanning Asia, North America, South America, and Europe to facilitate cross-border business opportunities and international partnerships."
        },
        {
          "@type": "Service",
          serviceType: "Asia Pacific Business Advisory",
          provider: {
            "@id": `${siteUrl}/#organization`
          },
          areaServed: [
            {
              "@type": "Country",
              name: "China"
            },
            {
              "@type": "Country",
              name: "Hong Kong"
            },
            {
              "@type": "Place",
              name: "Southeast Asia"
            }
          ],
          description: "Specialized consulting for businesses expanding into China, Hong Kong, and Southeast Asian markets with deep understanding of regional business practices and regulatory environments."
        },
        {
          "@type": "Service",
          serviceType: "Latin America Trade Facilitation",
          provider: {
            "@id": `${siteUrl}/#organization`
          },
          areaServed: {
            "@type": "Place",
            name: "South America"
          },
          description: "Strategic advisory for cross-border trade and business development opportunities in South American markets, leveraging Consulate relationships and regional expertise."
        },
        {
          "@type": "Service",
          serviceType: "Strategic Business Planning",
          provider: {
            "@id": `${siteUrl}/#organization`
          },
          description: "Comprehensive strategic planning for sustainable growth and competitive positioning in global markets."
        },
        {
          "@type": "Service",
          serviceType: "Financial Planning & Analysis",
          provider: {
            "@id": `${siteUrl}/#organization`
          },
          description: "Expert financial guidance for international expansion, cross-border transactions, and global business operations."
        },
        {
          "@type": "Service",
          serviceType: "Market Analysis & Research",
          provider: {
            "@id": `${siteUrl}/#organization`
          },
          description: "In-depth market research and analysis for international markets including China, Hong Kong, Southeast Asia, South America, and Canada."
        },
        {
          "@type": "Service",
          serviceType: "Operational Excellence",
          provider: {
            "@id": `${siteUrl}/#organization`
          },
          description: "Process optimization and operational efficiency for cross-border business operations and international supply chains."
        },
        {
          "@type": "Service",
          serviceType: "Organizational Development",
          provider: {
            "@id": `${siteUrl}/#organization`
          },
          description: "Building high-performing teams and organizational structures for global business expansion and cross-cultural management."
        }
      ]
    };

    // Create or update script tag
    const scriptId = `schema-${type}`;
    let scriptTag = document.getElementById(scriptId);
    
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = scriptId;
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    
    scriptTag.textContent = JSON.stringify(schemaData);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [type, data]);

  return null;
}
